import { Stack, Group } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { PortalButton } from 'components/Button/PortalButton';
import { useDrawerContext } from 'context/hooks';
import { CreateRuleFormProvider, useCreateRuleForm } from 'context/FormContext';
import { BsFloppy, BsSearch } from 'react-icons/bs';
import { RuleItem, RuleStatus, RuleStatuses } from 'models/rules';
import { useCreateRule } from 'hooks/api/rule/useCreateRule';
import { BasicInfoFields } from './Fields/BasicInfoFields';
import { RuleConfigurationFields } from './Fields/RuleConfigurationFields';
import { DefineConditionsFields } from './Fields/DefineConditionsFields';
import { useDefineRuleFormValidations } from './hooks/useDefineRuleFormValidations';

interface Props {
  createItem: Pick<RuleItem, 'patientId' | 'groupId'>;
}

export const DefineRuleForm = ({ createItem: { patientId, groupId } }: Props) => {
  const { t } = useTranslation();
  const { closeDrawer } = useDrawerContext();
  const { initialValues, validate } = useDefineRuleFormValidations();
  const form = useCreateRuleForm({ initialValues, validate });
  const { mutate: createRule } = useCreateRule({ reset: () => form.setFieldValue('formDisabled', false) });
  const { values } = form;

  const handleDrawerClose = () => {
    form.reset();
    closeDrawer();
  };

  const handleFormSave = (status: RuleStatus) => {
    form.validate();
    if (form.isValid()) {
      form.setFieldValue('formDisabled', true);
      createRule({ ...values, patientId, groupId, toStatus: status });
    }
  };

  return (
    <CreateRuleFormProvider form={form}>
      <Stack>
        <BasicInfoFields />
        <RuleConfigurationFields />
        <DefineConditionsFields />
        <Group mt={'xl'} justify={'flex-start'}>
          <PortalButton
            label={t('review_label')}
            Icon={BsSearch}
            disabled={values.formDisabled}
            onClick={() => handleFormSave(RuleStatuses.inReview)}
          />
          <PortalButton
            label={t('save_label')}
            Icon={BsFloppy}
            disabled={values.formDisabled}
            variant={'light'}
            onClick={() => handleFormSave(RuleStatuses.draft)}
          />
          <PortalButton
            variant={'outline'}
            onClick={handleDrawerClose}
            label={t('cancel_label')}
            disabled={values.formDisabled}
          />
        </Group>
      </Stack>
    </CreateRuleFormProvider>
  );
};
